import { useState, useEffect } from 'react';
import { feedImageProps } from '../services/imageUpload';

/**
 * <SmartImg /> — an <img> that gracefully swaps to a fallback React node
 * when the source is empty OR the fetch 404s. Combines with feedImageProps
 * so callers still get `loading="lazy"` + `srcset` for free.
 *
 * Usage:
 *   <SmartImg
 *     src={item.photo}
 *     alt={item.title}
 *     className="w-full h-full object-cover"
 *     fallback={<Wrench className="text-stone-300" />}
 *   />
 *
 * The fallback renders inside a plain <span> that inherits the same
 * classes so it fills the exact same box as the image would.
 */
export default function SmartImg({
  src,
  fallback,
  alt = '',
  className = '',
  fallbackClassName,
  ...imgProps
}) {
  const [failed, setFailed] = useState(false);
  // Reset the fallback whenever the source changes so a reused component
  // (carousels, edit-then-save, paginated rows) can recover a valid URL.
  useEffect(() => setFailed(false), [src]);
  const empty = !src;
  if (empty || failed) {
    return (
      <span
        className={`${fallbackClassName ?? className} flex items-center justify-center bg-stone-100`}
        role="img"
        aria-label={alt || 'image unavailable'}
        data-testid="smart-img-fallback"
      >
        {fallback}
      </span>
    );
  }
  return (
    <img
      alt={alt}
      className={className}
      onError={() => setFailed(true)}
      {...feedImageProps(src)}
      {...imgProps}
    />
  );
}
